import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import ProfileCard from '../components/ProfileCard';
import { Search, Filter, X, Loader2, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import GooglePlacesInput from '../components/GooglePlacesInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FilterSelect({ value, onChange, placeholder, options, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 px-3 py-2 rounded-xl bg-white border border-[hsl(35,15%,85%)] text-[hsl(20,10%,27%)] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(144,18%,80%)] focus:border-[hsl(144,18%,45%)]"
      data-testid={testId}>
      <option value="all">{placeholder}</option>
      {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  );
}

function NeighborhoodMultiSelect({ values, onChange }) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const fetchSuggestions = async (q) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    try {
      const res = await axios.get(`${API}/neighborhoods?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setSuggestions(res.data.neighborhoods || []);
    } catch { setSuggestions([]); }
  };

  const addZone = (zone) => {
    const clean = zone.trim();
    if (!clean || values.includes(clean) || values.length >= 4) return;
    onChange([...values, clean]);
    setInputValue(''); setSuggestions([]);
  };

  const removeZone = (zone) => onChange(values.filter(v => v !== zone));

  return (
    <div className="space-y-2">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map(zone => (
            <span key={zone} className="inline-flex items-center gap-1 bg-[hsl(144,18%,88%)] text-[hsl(144,18%,28%)] text-xs font-medium px-2.5 py-1 rounded-full">
              <span className="capitalize">{zone}</span>
              <button type="button" onClick={() => removeZone(zone)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      {values.length < 4 && (
        <div className="relative">
          <GooglePlacesInput type="neighborhood" value={inputValue}
            onChange={(val) => { setInputValue(val); fetchSuggestions(val); }}
            onSelect={(val) => addZone(val)}
            placeholder={values.length === 0 ? 'Ej: Malasaña, Gràcia...' : 'Añadir otra zona...'}
            className="rounded-xl w-full h-10 px-3 py-2 bg-white border border-[hsl(35,15%,85%)] text-[hsl(20,10%,27%)] text-sm" />
          {inputValue.trim() && (
            <button type="button" onClick={() => addZone(inputValue)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(144,18%,45%)]"><Plus className="w-4 h-4" /></button>
          )}
          {suggestions.length > 0 && (
            <ul className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-[hsl(35,15%,85%)] rounded-xl shadow-md overflow-hidden max-h-40 overflow-y-auto">
              {suggestions.map(s => <li key={s} onClick={() => addZone(s)} className="px-3 py-2 text-sm cursor-pointer hover:bg-[hsl(144,18%,93%)] capitalize">{s}</li>)}
            </ul>
          )}
        </div>
      )}
      {values.length >= 4 && <p className="text-xs text-[hsl(20,8%,55%)]">Máximo 4 zonas seleccionadas</p>}
    </div>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    city: '', neighborhoods: [], min_budget: '', max_budget: '',
    min_age: '', max_age: '', is_smoker: 'all', has_pets: 'all',
    gender: 'all', preferred_flatmate_gender: 'all',
    num_flatmates: 'all', stay_duration: 'all',
  });

  const sentinelRef = useRef(null);
  const stateRef = useRef({ page: 1, totalPages: 1, loading: true, loadingMore: false, filters });

  useEffect(() => { stateRef.current = { page, totalPages, loading, loadingMore, filters }; });

  const buildParams = (pageNum, f) => {
    const p = new URLSearchParams();
    p.append('page', pageNum.toString());
    p.append('limit', '12');
    if (f.city) p.append('city', f.city);
    if (f.neighborhoods.length > 0) p.append('neighborhoods', f.neighborhoods.join(','));
    if (f.min_budget) p.append('min_budget', f.min_budget);
    if (f.max_budget) p.append('max_budget', f.max_budget);
    if (f.min_age) p.append('min_age', f.min_age);
    if (f.max_age) p.append('max_age', f.max_age);
    if (f.is_smoker !== 'all') p.append('is_smoker', f.is_smoker);
    if (f.has_pets !== 'all') p.append('has_pets', f.has_pets);
    if (f.gender !== 'all') p.append('gender', f.gender);
    if (f.preferred_flatmate_gender !== 'all') p.append('preferred_flatmate_gender', f.preferred_flatmate_gender);
    if (f.num_flatmates !== 'all') p.append('num_flatmates', f.num_flatmates);
    if (f.stay_duration !== 'all') p.append('stay_duration', f.stay_duration);
    return p;
  };

  const handleRemove = useCallback((userId) => {
    setProfiles(prev => prev.filter(p => p.user_id !== userId));
  }, []);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true); setPage(1); setTotalPages(1);
      try {
        const res = await axios.get(`${API}/profiles?${buildParams(1, filters)}`, { withCredentials: true });
        setProfiles(res.data.profiles || []);
        setTotalPages(res.data.pages || 1);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [filters]); // eslint-disable-line

  const doLoadMore = async () => {
    const s = stateRef.current;
    if (s.loading || s.loadingMore || s.page >= s.totalPages) return;
    setLoadingMore(true);
    const next = s.page + 1;
    try {
      const res = await axios.get(`${API}/profiles?${buildParams(next, s.filters)}`, { withCredentials: true });
      const newProfiles = res.data.profiles || [];
      setProfiles(prev => {
        const ids = new Set(prev.map(p => p.user_id));
        return [...prev, ...newProfiles.filter(p => !ids.has(p.user_id))];
      });
      setPage(next);
    } catch (e) { console.error(e); }
    finally { setLoadingMore(false); }
  };

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) doLoadMore(); },
      { threshold: 0, rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []); // eslint-disable-line

  const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));
  const clearFilters = () => setFilters({
    city: '', neighborhoods: [], min_budget: '', max_budget: '',
    min_age: '', max_age: '', is_smoker: 'all', has_pets: 'all',
    gender: 'all', preferred_flatmate_gender: 'all',
    num_flatmates: 'all', stay_duration: 'all',
  });
  const hasActiveFilters = filters.city !== '' || filters.neighborhoods.length > 0 || filters.min_budget !== '' || filters.max_budget !== '' || filters.min_age !== '' || filters.max_age !== '' || filters.is_smoker !== 'all' || filters.has_pets !== 'all' || filters.gender !== 'all' || filters.preferred_flatmate_gender !== 'all' || filters.num_flatmates !== 'all' || filters.stay_duration !== 'all';

  const smokerOptions = [{ value: 'false', label: 'No fumador' }, { value: 'true', label: 'Fumador' }];
  const petOptions = [{ value: 'false', label: 'Sin mascotas' }, { value: 'true', label: 'Con mascotas' }];
  // Género del perfil (quién es la persona)
  const genderOptions = [{ value: 'male', label: 'Hombres' }, { value: 'female', label: 'Mujeres' }, { value: 'prefer_not_to_say', label: 'Otro género' }];
  // Con quién quieren vivir
  const preferredFlatmateGenderOptions = [{ value: 'male', label: 'Buscan hombres' }, { value: 'female', label: 'Buscan mujeres' }, { value: 'any', label: 'Sin preferencia' }];
  const numFlatmatesOptions = [{ value: '1', label: '1 compañero' }, { value: '2', label: '2 compañeros' }, { value: '3_or_more', label: '3 o más compañeros' }, { value: 'any', label: 'Indiferente' }];
  const stayDurationOptions = [{ value: '1-3 meses', label: '1-3 meses' }, { value: '3-6 meses', label: '3-6 meses' }, { value: '6-12 meses', label: '6-12 meses' }, { value: 'Más de 1 año', label: 'Más de 1 año' }, { value: 'Indefinido', label: 'Indefinido' }];

  if (user && !user.profile_completed) {
    return (
      <div className="min-h-screen bg-[hsl(35,25%,91%)]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="font-heading font-bold text-2xl text-[hsl(20,10%,27%)] mb-4">Completa tu perfil</h2>
          <p className="text-[hsl(20,8%,46%)] mb-8">Para ver y contactar con otros usuarios, primero necesitas completar tu perfil.</p>
          <Link to="/onboarding"><Button className="bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-full">Completar perfil</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(35,25%,91%)]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        <div className="space-y-4 mb-10">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(20,8%,55%)] pointer-events-none z-10" />
              <GooglePlacesInput value={filters.city} onChange={(value) => handleFilterChange('city', value)} placeholder="Buscar por ciudad..." className="pl-10 rounded-xl bg-white border border-[hsl(35,15%,85%)] w-full h-10 px-3 py-2" />
            </div>
            <Button variant="outline" className="rounded-xl border-[hsl(35,15%,85%)] text-[hsl(20,10%,40%)] hover:bg-[hsl(35,25%,85%)] relative" onClick={() => setFiltersOpen(!filtersOpen)} data-testid="filters-btn">
              <Filter className="w-4 h-4 mr-2" />Filtros
              {hasActiveFilters && <span className="absolute -top-1 -right-1 w-3 h-3 bg-[hsl(18,45%,63%)] rounded-full" />}
              {filtersOpen ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
            </Button>
          </div>
          {filtersOpen && (
            <div className="bg-white rounded-2xl border border-[hsl(35,15%,88%)] p-6 shadow-sm space-y-5">
              <div>
                <Label className="text-sm text-[hsl(20,8%,46%)]">Zonas / Barrios <span className="ml-1 text-xs text-[hsl(20,8%,60%)]">(hasta 4)</span></Label>
                <div className="mt-1"><NeighborhoodMultiSelect values={filters.neighborhoods} onChange={(v) => handleFilterChange('neighborhoods', v)} /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div><Label className="text-sm text-[hsl(20,8%,46%)]">Presupuesto mínimo (€)</Label><Input type="number" placeholder="Mín" value={filters.min_budget} onChange={(e) => handleFilterChange('min_budget', e.target.value)} className="mt-1 rounded-xl" /></div>
                <div><Label className="text-sm text-[hsl(20,8%,46%)]">Presupuesto máximo (€)</Label><Input type="number" placeholder="Máx" value={filters.max_budget} onChange={(e) => handleFilterChange('max_budget', e.target.value)} className="mt-1 rounded-xl" /></div>
                <div><Label className="text-sm text-[hsl(20,8%,46%)]">Edad mínima</Label><Input type="number" placeholder="Mín" value={filters.min_age} onChange={(e) => handleFilterChange('min_age', e.target.value)} className="mt-1 rounded-xl" /></div>
                <div><Label className="text-sm text-[hsl(20,8%,46%)]">Edad máxima</Label><Input type="number" placeholder="Máx" value={filters.max_age} onChange={(e) => handleFilterChange('max_age', e.target.value)} className="mt-1 rounded-xl" /></div>
                <div><Label className="text-sm text-[hsl(20,8%,46%)]">Género</Label><div className="mt-1"><FilterSelect value={filters.gender} onChange={(v) => handleFilterChange('gender', v)} placeholder="Todos los géneros" options={genderOptions} testId="gender-filter-select" /></div></div>
                <div><Label className="text-sm text-[hsl(20,8%,46%)]">Buscan convivir con</Label><div className="mt-1"><FilterSelect value={filters.preferred_flatmate_gender} onChange={(v) => handleFilterChange('preferred_flatmate_gender', v)} placeholder="Sin preferencia" options={preferredFlatmateGenderOptions} testId="preferred-flatmate-gender-filter-select" /></div></div>
                <div><Label className="text-sm text-[hsl(20,8%,46%)]">Fumador</Label><div className="mt-1"><FilterSelect value={filters.is_smoker} onChange={(v) => handleFilterChange('is_smoker', v)} placeholder="Sin preferencia" options={smokerOptions} testId="smoker-filter-select" /></div></div>
                <div><Label className="text-sm text-[hsl(20,8%,46%)]">Mascotas</Label><div className="mt-1"><FilterSelect value={filters.has_pets} onChange={(v) => handleFilterChange('has_pets', v)} placeholder="Sin preferencia" options={petOptions} testId="pets-filter-select" /></div></div>
                <div><Label className="text-sm text-[hsl(20,8%,46%)]">Nº de compañeros</Label><div className="mt-1"><FilterSelect value={filters.num_flatmates} onChange={(v) => handleFilterChange('num_flatmates', v)} placeholder="Indiferente" options={numFlatmatesOptions} testId="num-flatmates-filter-select" /></div></div>
                <div><Label className="text-sm text-[hsl(20,8%,46%)]">Duración de estancia</Label><div className="mt-1"><FilterSelect value={filters.stay_duration} onChange={(v) => handleFilterChange('stay_duration', v)} placeholder="Cualquier duración" options={stayDurationOptions} testId="stay-duration-filter-select" /></div></div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={clearFilters} className="flex-1 rounded-xl border-[hsl(35,15%,85%)]"><X className="w-4 h-4 mr-2" />Limpiar</Button>
                <Button onClick={() => setFiltersOpen(false)} className="flex-1 bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-xl">Aplicar</Button>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[hsl(144,18%,45%)]" />
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[hsl(20,8%,46%)] mb-4">No se encontraron perfiles con estos filtros.</p>
            {hasActiveFilters && <Button variant="outline" onClick={clearFilters} className="rounded-xl border-[hsl(35,15%,85%)]">Limpiar filtros</Button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="profiles-grid">
            {profiles.map((profile) => (
              <ProfileCard key={profile.user_id} profile={profile} onRemove={handleRemove} />
            ))}
          </div>
        )}

        <div ref={sentinelRef} className="h-4 mt-8" />

        {loadingMore && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-[hsl(144,18%,45%)]" />
          </div>
        )}

        {!loading && !loadingMore && page < totalPages && (
          <div className="flex justify-center mt-4">
            <Button variant="outline" onClick={doLoadMore} className="rounded-xl border-[hsl(35,15%,85%)] text-[hsl(20,10%,40%)]">
              Cargar más perfiles
            </Button>
          </div>
        )}

        {!loading && !loadingMore && page >= totalPages && profiles.length > 0 && (
          <p className="text-center text-sm text-[hsl(20,8%,55%)] py-4">Has visto todos los perfiles disponibles</p>
        )}
      </main>
    </div>
  );
}
